import { ArrowUpRight } from "lucide-react"
import { useState } from "react"
import { useComputers } from "../../computers/context"
import { PersonGlyph } from "../computers/PersonGlyph"
import { Section } from "./Section"
import { levelWord } from "./sharingCopy"

/**
 * Computers colleagues opened to you. Each row names the person first,
 * because that is how you think of it: Sam's computer, not node_8f2a.
 */
export function SharedWithYou({ order }: { order: number }) {
    const computers = useComputers()
    const [confirmNodeId, setConfirmNodeId] = useState<string | null>(null)
    if (computers.people.length === 0) return null

    return (
        <Section label="Shared with you" order={order}>
            <div className="divide-y divide-border border border-border bg-base-200">
                {computers.people.map((person) => {
                    const computer = computers.state.computers.find((candidate) => candidate.nodeId === person.nodeId)
                    const active = computers.scope === person.nodeId
                    const confirming = confirmNodeId === person.nodeId
                    const where = computer?.name ?? `${person.personName}'s computer`
                    return (
                        <div key={person.nodeId} className="flex items-center gap-3 px-4 py-3" data-shared-row={person.nodeId}>
                            <PersonGlyph name={person.personName} glyph={person.glyph} color={person.color} size="lg" />
                            <div className="min-w-0 flex-1">
                                <div className="truncate text-sm font-medium text-base-content">{person.personName}</div>
                                <div className="mt-0.5 truncate text-xs text-muted">
                                    {person.level === "none" || person.projectNames.length === 0
                                        ? `Linked · nothing shared with you yet · ${where}`
                                        : `${levelWord(person.level)} · ${person.projectNames.join(", ")} on ${where}`}
                                </div>
                            </div>
                            {confirming ? (
                                <div className="flex flex-none items-center gap-1">
                                    <span className="mr-1 text-xs text-muted">Leave {where}?</span>
                                    <button
                                        type="button"
                                        className="btn flex h-8 items-center justify-center bg-error px-3 text-xs font-medium text-error-content"
                                        onClick={() => {
                                            setConfirmNodeId(null)
                                            void computers.removeComputer(person.nodeId)
                                        }}
                                    >
                                        Leave
                                    </button>
                                    <button
                                        type="button"
                                        className="btn flex h-8 items-center justify-center px-3 text-xs text-muted hover:text-base-content"
                                        onClick={() => setConfirmNodeId(null)}
                                    >
                                        Stay
                                    </button>
                                </div>
                            ) : (
                                <div className="flex flex-none items-center gap-1">
                                    {!active && (
                                        <button
                                            type="button"
                                            className="btn flex h-8 items-center gap-1 border border-border bg-base-100 px-2.5 text-xs font-medium text-base-content hover:bg-base-300/60"
                                            onClick={() => void computers.selectScope(person.nodeId)}
                                        >
                                            Open
                                            <ArrowUpRight className="h-3.5 w-3.5" />
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        className="btn flex h-8 items-center justify-center px-2.5 text-xs text-muted hover:text-error"
                                        onClick={() => setConfirmNodeId(person.nodeId)}
                                    >
                                        Leave
                                    </button>
                                </div>
                            )}
                        </div>
                    )
                })}
            </div>
        </Section>
    )
}
